import { useEffect, useState, useMemo } from "react";
import { Row, Col } from "antd";
import CustomButton from "../../common/CustomButton";
import CustomSelect from "../../common/CustomSelect";
import { getAllCrewEmployeesWithoutPaginationApi } from "../../apis/crewEmployee";

const roleLimits = {
  ALS: { driver: 2, emt: 2, helper: 2 },
  BLS: { driver: 2, emt: 2, helper: 0 },
  MV: { driver: 2, emt: 0, helper: 2 },
};

function AssignCrewMembers({ selectedAmbulanceDetails, onSubmit, errorMsg, successMsg, initialEmployees = [] }) {
  const [crewMembers, setCrewMembers] = useState([]);
  const [validationError, setValidationError] = useState("");
  const [selectedEmployees, setSelectedEmployees] = useState({
    driver: [],
    emt: [],
    helper: [],
  });

  useEffect(() => {
    async function fetchCrewMembers() {
      try {
        const response = await getAllCrewEmployeesWithoutPaginationApi();
        if (response) {
          setCrewMembers(response);
        }
      } catch (error) {
        console.error("Failed to fetch crew members:", error);
      }
    }

    fetchCrewMembers();
  }, []);

  useEffect(() => {
    if (!crewMembers.length || !initialEmployees.length) return;

    const mapped = { driver: [], emt: [], helper: [] };

    initialEmployees.forEach((emp) => {
      const exists = crewMembers.find((cm) => cm.employeeId === emp.employeeId);
      if (!exists) return;

      if (emp.employeeRole === "DRIVER") mapped.driver.push(emp.employeeId);
      if (emp.employeeRole === "EMT") mapped.emt.push(emp.employeeId);
      if (emp.employeeRole === "HELPER") mapped.helper.push(emp.employeeId);
    });

    setSelectedEmployees(mapped);
  }, [initialEmployees, crewMembers]);

  const drivers = useMemo(
    () => crewMembers.filter((emp) => emp.employeeRole === "DRIVER"),
    [crewMembers]
  );
  
  const emts = useMemo(
    () => crewMembers.filter((emp) => emp.employeeRole === "EMT"),
    [crewMembers]
  );
  
  const helpers = useMemo(
    () => crewMembers.filter((emp) => emp.employeeRole === "HELPER"),
    [crewMembers]
  );

  const handleClick = () => {
    const typeRaw = selectedAmbulanceDetails?.ambulance?.type?.id || "";
    let limits;
    if (typeRaw === "ALS") {
      limits = { driver: 2, emt: 2, helper: 2 };
    } else if (typeRaw.startsWith("BLS")) {
      limits = { driver: 2, emt: 2, helper: 0 };
    } else if (typeRaw === "MV") {
      limits = { driver: 2, emt: 0, helper: 2 };
    } else {
      limits = { driver: 0, emt: 0, helper: 0 };
    }
  
    const countDriver = selectedEmployees.driver.length;
    const countEmt = selectedEmployees.emt.length;
    const countHelper = selectedEmployees.helper.length;
  
    if (countDriver > limits.driver) {
      setValidationError(`Maximum ${limits.driver} driver(s) allowed for ${typeRaw} ambulance`);
      return;
    }
  
    if (countEmt > limits.emt) {
      setValidationError(`Maximum ${limits.emt} EMT(s) allowed for ${typeRaw} ambulance`);
      return;
    }
  
    if (countHelper > limits.helper) {
      setValidationError(`Maximum ${limits.helper} helper(s) allowed for ${typeRaw} ambulance`);
      return;
    }
  
    setValidationError("");
  
    const query = new URLSearchParams();
    const allIds = [
      ...selectedEmployees.driver,
      ...selectedEmployees.emt,
      ...selectedEmployees.helper,
    ];
    
    allIds.forEach((id) => query.append("employeeIds", id));
  
    if (selectedAmbulanceDetails?.ambulance?.id) {
      query.append("ambulanceId", selectedAmbulanceDetails.ambulance.id);
    }
  
    query.append("isGeofencingEnabled", true);
    onSubmit(query.toString());
  };  

  return (
    <div style={{ width: "100%" }}>
      <Row gutter={16}>
        <Col span={8}>
          <CustomSelect
            label="Select Driver"
            placeholder="Select Driver"
            value={selectedEmployees.driver}
            onChange={(value) => setSelectedEmployees((prev) => ({ ...prev, driver: value }))}
            options={drivers.map((driver) => ({
              value: driver.employeeId,
              label: driver.employeeName,
            }))}
            style={{ width: "100%" }}
            mode="multiple"
          />
        </Col>

        <Col span={8}>
          <CustomSelect
            label="Select EMT(s)"
            placeholder="Select EMT(s)"
            value={selectedEmployees.emt}
            onChange={(value) => setSelectedEmployees((prev) => ({ ...prev, emt: value }))}
            options={emts.map((emt) => ({
              value: emt.employeeId,
              label: emt.employeeName,
            }))}
            style={{ width: "100%" }}
            mode="multiple"
          />
        </Col>

        <Col span={8}>
          <CustomSelect
            label="Select Helper(s)"
            placeholder="Select Helper(s)"
            value={selectedEmployees.helper}
            onChange={(value) => setSelectedEmployees((prev) => ({ ...prev, helper: value }))}
            options={helpers.map((helper) => ({
              value: helper.employeeId,
              label: helper.employeeName,
            }))}
            style={{ width: "100%" }}
            mode="multiple"
          />
        </Col>
      </Row>

      <CustomButton title="Add Employee" onClick={handleClick} style={{ margin: "1rem 0" }} />
      {errorMsg && <label style={{ color: "red" }}>{errorMsg}</label>}
      {successMsg && <label style={{ color: "green" }}>{successMsg}</label>}
      {validationError && <label style={{ color: "red" }}>{validationError}</label>}
      {errorMsg && !validationError && <label style={{ color: "red" }}>{errorMsg}</label>}
      {successMsg && !validationError && <label style={{ color: "green" }}>{successMsg}</label>}
    </div>
  );
}

export default AssignCrewMembers;
